import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Dropdown, Field, Label, ValidationMessage } from '@digdir/designsystemet-react'
import type { FieldSize } from './Select'
import { Icon } from './Icon'

export type SelectMenuOption = {
  value: string
  label: string
}

export type SelectMenuProps = {
  /** Visible label (pre-translated by the caller). */
  label: string
  /** Controlled value; an empty string is a real choice when present in options. */
  value: string
  /** Called with the new value when an option is chosen. */
  onChange: (value: string) => void
  options: SelectMenuOption[]
  /** Shown when no option is selected. */
  placeholder?: string
  hint?: string
  error?: string
  required?: boolean
  disabled?: boolean
  /** Optional content-width cap (desktop only). Default: full width. */
  size?: FieldSize
}

// Selection stays owner-controlled; focus stays on the button, not in the list.
// DS owns the native manual-popover surface, without menu keyboard semantics.
export function SelectMenu({
  label,
  value,
  onChange,
  options,
  placeholder,
  hint,
  error,
  required = false,
  disabled = false,
  size,
}: SelectMenuProps) {
  const id = useId()
  const listId = `${id}-list`
  const errorId = error ? `${id}-err` : undefined
  const hintId = hint ? `${id}-hint` : undefined
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [activeValue, setActiveValue] = useState<string | null>(null)
  const ignoreDsClose = useRef(false)
  const expanded = open && !disabled

  const selected = options.find((o) => o.value === value)
  const highlighted = options.findIndex((o) => o.value === activeValue)
  const active = highlighted < 0 ? Math.max(0, options.findIndex((o) => o.value === value)) : highlighted

  const place = useCallback(() => {
    const list = listRef.current
    const trigger = triggerRef.current
    // A native popover is display:none until DS opens it in its passive effect.
    if (!list?.matches(':popover-open') || !trigger) return
    const rect = trigger.getBoundingClientRect()
    const below = Math.max(0, window.innerHeight - rect.bottom - 12)
    const above = Math.max(0, rect.top - 12)
    const onTop = below < Math.min(260, list.scrollHeight) && above > below
    const maxHeight = Math.min(260, onTop ? above : below)
    const width = Math.min(rect.width, Math.max(0, window.innerWidth - 16))
    Object.assign(list.style, {
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`,
    })
    const height = list.getBoundingClientRect().height
    list.style.top = `${Math.max(8, Math.min(onTop ? rect.top - height - 4 : rect.bottom + 4, window.innerHeight - height - 8))}px`
  }, [])

  // Register before DS's document capture listener. A trigger click owns its
  // toggle; DS must not first close it and then let that click reopen it.
  useLayoutEffect(() => {
    const onClick = (e: MouseEvent) => {
      ignoreDsClose.current = !!triggerRef.current?.contains(e.target as Node)
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => {
    if (!expanded) return
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [expanded, options, place])

  useEffect(() => {
    if (!expanded) return
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' })
  }, [expanded, active, options])

  useEffect(() => {
    setActiveValue(null)
  }, [value])

  useEffect(() => {
    if (!expanded) return
    const onOutside = (e: PointerEvent) => {
      const target = e.target as Node
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onOutside, true)
    return () => document.removeEventListener('pointerdown', onOutside, true)
  }, [expanded])

  function choose(index: number) {
    const option = options[index]
    if (disabled || triggerRef.current?.matches(':disabled') || !expanded || !option) return
    setOpen(false)
    triggerRef.current?.focus()
    onChange(option.value)
  }

  function toggle() {
    if (disabled || triggerRef.current?.matches(':disabled')) return
    triggerRef.current?.focus()
    setActiveValue(selected?.value ?? options[0]?.value ?? null)
    setOpen((o) => !o)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled || triggerRef.current?.matches(':disabled')) return
    if (!expanded && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault()
      e.stopPropagation()
      toggle()
      return
    }
    if (!expanded) return
    if (e.key === 'Tab') {
      setOpen(false)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      const next = Math.max(0, Math.min(options.length - 1, active + (e.key === 'ArrowDown' ? 1 : -1)))
      setActiveValue(options[next]?.value ?? null)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      choose(active)
    }
  }

  return (
    <Field className={size ? `field field--w-${size}` : 'field'}>
      <Label className="field__lbl" htmlFor={id}>
        {label}
      </Label>
      {hint && <Field.Description id={hintId} className="field__hint">{hint}</Field.Description>}

      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        id={id}
        className="selectmenu__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-activedescendant={expanded && options[active] ? `${listId}-${active}` : undefined}
        data-placeholder={!selected || undefined}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); toggle() }}
        onKeyDown={onKeyDown}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : (placeholder ?? '')}
        </span>
        <Icon name="chevronD" size={18} aria-hidden="true" />
      </Button>

      {expanded &&
        createPortal(
        <Dropdown
          asChild
          open
          id={listId}
          placement="none"
          onClose={() => { if (!ignoreDsClose.current) setOpen(false) }}
          onToggle={place}
          className="selectmenu__list"
          role="listbox"
          aria-label={label}
          style={{
            position: 'fixed',
            inset: 'auto',
            translate: 'none',
            margin: 0,
            listStyle: 'none',
            minWidth: 0,
            overflowY: 'auto',
          }}
        >
          <ul ref={listRef} onKeyDown={onKeyDown} onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
            {options.map((o, i) => (
              <Button asChild variant="tertiary" key={o.value}>
                <li
                  id={`${listId}-${i}`}
                  className="selectmenu__option"
                  role="option"
                  tabIndex={-1}
                  aria-selected={o.value === value}
                  data-active={i === active || undefined}
                  onMouseEnter={() => setActiveValue(o.value)}
                  onClick={() => choose(i)}
                >
                  {o.label}
                </li>
              </Button>
            ))}
          </ul>
        </Dropdown>,
          triggerRef.current?.closest('[popover],dialog') ?? document.body,
        )}

      {error && (
        <ValidationMessage id={errorId} className="field__err" role="alert">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}
